import { EventEmitter, Injectable } from '@angular/core';
import { filter, map, merge, Observable, of, Subscription } from "rxjs";
import { AvgDeltaTime, FlexRicState, FlexRicTelemetryDto, MachineId, StompService, TenantId } from "@allbesmart/web-app-common";
import { environment } from "../../environments/environment";
import { WS_SUFFIX } from "../external/ws";

@Injectable({
  providedIn: 'root'
})
export class FlexRicTelemetryService {

  private machineFlexRicTelemetryChanged$: EventEmitter<{
    machineId: MachineId,
    telemetry: FlexRicTelemetryDto
  }> = new EventEmitter();

  private flexRicAvgTimeMap: Map<MachineId, AvgDeltaTime> = new Map();
  private flexRicOfflineTimeoutMap: Map<MachineId, NodeJS.Timeout> = new Map();
  private flexRicTelemetryMap: Map<MachineId, FlexRicTelemetryDto> = new Map();
  private flexRicTelemetrySubscriptionMap: Map<MachineId, Subscription> = new Map();

  constructor(
    private stompService: StompService,
  ) {
  }

  public getFlexRicTelemetry$(machineId: MachineId): Observable<FlexRicTelemetryDto> {
    const flexRicTelemetry = this.flexRicTelemetryMap.get(machineId);
    const machineFlexRicTelemetry$ = this.machineFlexRicTelemetryChanged$.pipe(
      filter(changedTelemetry => changedTelemetry.machineId === machineId),
      map(changedTelemetry => changedTelemetry.telemetry)
    );
    if (flexRicTelemetry) {
      return merge(of(flexRicTelemetry), machineFlexRicTelemetry$);
    } else {
      return machineFlexRicTelemetry$;
    }
  }

  public listenToFlexRicTelemetry(tenantId: TenantId, machineId: MachineId): void {
    if (!this.flexRicTelemetrySubscriptionMap.has(machineId)) {
      this.flexRicTelemetrySubscriptionMap.set(machineId, this.stompService
        .subscribeTopic<FlexRicTelemetryDto>(`${tenantId}.${machineId}.${WS_SUFFIX.FLEX_RIC_TELEMETRY}`)
        .subscribe(flexRicTelemetryDto => {
          let flexRicAvgTime = this.flexRicAvgTimeMap.get(machineId);
          if (flexRicAvgTime === undefined) this.flexRicAvgTimeMap
            .set(machineId, flexRicAvgTime = new AvgDeltaTime(environment.defaultFlexRicOfflineMs));

          this.flexRicTelemetryMap.set(machineId, flexRicTelemetryDto);
          this.machineFlexRicTelemetryChanged$.emit({machineId: machineId, telemetry: flexRicTelemetryDto});

          clearTimeout(this.flexRicOfflineTimeoutMap.get(machineId));
          this.flexRicOfflineTimeoutMap.set(machineId, setTimeout(() => {
            flexRicTelemetryDto.state = FlexRicState.STOPPED;
            this.machineFlexRicTelemetryChanged$.emit({machineId: machineId, telemetry: flexRicTelemetryDto});
          }, flexRicAvgTime.getDeltaTime(Date.now()) * 3));
        }));
    }
  }

  public stopListeningFlexRicTelemetry(machineId: MachineId): void {
    this.flexRicTelemetrySubscriptionMap.get(machineId)?.unsubscribe();
    this.flexRicTelemetrySubscriptionMap.delete(machineId);

    clearTimeout(this.flexRicOfflineTimeoutMap.get(machineId));
    this.flexRicOfflineTimeoutMap.delete(machineId);

    this.flexRicAvgTimeMap.set(machineId, new AvgDeltaTime(environment.defaultFlexRicOfflineMs));
  }
}
